import { GroupAgentPreviewBanner } from '@ms/yammer-groups';
import {
  useAndReportExperimentTreatment,
  useExperimentTreatmentWithoutReporting,
} from '@ms/yammer-hooks/dist/experiments';
import { useIsPrivateContentModeActiveForViewer } from '@ms/yammer-hooks/dist/featureAccessSettings/selectors';
import { useIsCurrentUserRestricted } from '@ms/yammer-hooks/dist/session/selectors';
import { useIsCurrentUserAdmin } from '@ms/yammer-hooks/dist/users';
import { useViewerIsPreviewing } from '@ms/yammer-network-brand-theme/dist/store';
import PrivateContentModeBanner from '@ms/yammer-web-components/dist/admin/PrivateContentModeBanner';
import { useLeadershipCornerBanner } from '@ms/yammer-web-components/dist/leadershipCorner/LeadershipCornerBanner';
import { useOutageBanner } from '@ms/yammer-web-components/dist/outageBanner/hooks';
import RestrictedUserBanner from '@ms/yammer-web-components/dist/user/RestrictedUserBanner';
import Flex, { FlexItem } from '@ms/yammer-web-core-ui/dist/components/Flex';
import React, { Suspense, lazy, useMemo } from 'react';

const BrandCenterPreviewBanner = lazy(() =>
  import(
    /* webpackChunkName: "3-render-admin-brand-center" */ '@ms/yammer-admin-center/dist/BrandCenterPreviewBanner'
  ).then((module) => ({
    default: module.BrandCenterPreviewBanner,
  }))
);

export const useGetBanners = () => {
  const leadershipCornerBanner = useLeadershipCornerBanner();
  const shouldShowRestrictedUserBanner = useIsCurrentUserRestricted();
  const outageBanner = useOutageBanner();
  const isPrivateContentModeActive = useIsPrivateContentModeActiveForViewer();
  const isPreviewExperimentEnabled = useAndReportExperimentTreatment('CommunityAgentPublicPreview') === 'enabled';
  const isNetworkAdmin = useIsCurrentUserAdmin();
  const viewerIsPreviewing = useViewerIsPreviewing();
  const isCustomThemingEnabled = useExperimentTreatmentWithoutReporting('EngageCustomBrandingTheming') === true;
  const hasBrandCenterPreviewBanner = isCustomThemingEnabled && viewerIsPreviewing;

  return useMemo(() => {
    const showGroupAgentBanner = isNetworkAdmin && isPreviewExperimentEnabled;
    const groupAgentPreviewBanner = showGroupAgentBanner ? <GroupAgentPreviewBanner /> : null;
    const leadershipCornerBannerContent = leadershipCornerBanner ? <FlexItem>{leadershipCornerBanner}</FlexItem> : null;
    const restrictedUserBannerContent = shouldShowRestrictedUserBanner ? (
      <FlexItem>
        <RestrictedUserBanner />
      </FlexItem>
    ) : null;
    const outageBannerContent = outageBanner ? <FlexItem>{outageBanner}</FlexItem> : null;
    const privateContentModeBannerContent = isPrivateContentModeActive ? (
      <FlexItem>
        <PrivateContentModeBanner />
      </FlexItem>
    ) : null;
    const previewCustomThemingAdminPreviewContent = hasBrandCenterPreviewBanner ? (
      <Suspense fallback={null}>
        <FlexItem>
          <BrandCenterPreviewBanner />
        </FlexItem>
      </Suspense>
    ) : null;

    const hasAnyBanner =
      leadershipCornerBanner ||
      shouldShowRestrictedUserBanner ||
      outageBanner ||
      isPrivateContentModeActive ||
      showGroupAgentBanner ||
      hasBrandCenterPreviewBanner;

    const renderContent = hasAnyBanner ? (
      <Flex grow={1} direction="column">
        {privateContentModeBannerContent}
        {leadershipCornerBannerContent}
        {restrictedUserBannerContent}
        {outageBannerContent}
        {previewCustomThemingAdminPreviewContent}
        {groupAgentPreviewBanner}
      </Flex>
    ) : null;

    return renderContent;
  }, [
    leadershipCornerBanner,
    outageBanner,
    shouldShowRestrictedUserBanner,
    isPrivateContentModeActive,
    isNetworkAdmin,
    isPreviewExperimentEnabled,
    hasBrandCenterPreviewBanner,
  ]);
};
